import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { OnDestroyHookDirective } from '../common/hooks';
import {
  IOdbCoverDetailDivisionSummaryCategories,
  IOdbBillProductSplit,
  IBillingDocument,
  IBillingAccount,
  OdbCoverDetailDivision,
  IOdbBillCategoryDetailSection,
  coverService,
  IOdbBillCategoryDetailSectionItem,
} from '@dch/billing-business-logic';
import { SelectedOdbDetailsInfo } from '../models/pii.model';
import {
  getProductIdsFromSections,
  ODB_DETAILS_SESSION_DATA_KEY,
  UtilsService,
} from '../services/utils.service';
import { EnvironmentService } from '@dch/ng-common';
import {
  ONE_DIGITAL_BILL_USAGE_DET_L4_PAGE,
  OVERVIEW_PAGE,
} from '../config/my-bill.constants';
import { TranslationService } from '@dch/ng-common';
import { ApplicationState } from 'src/app/state/reducers';
import { Store } from '@ngrx/store';
import {
  selectCustomer,
  selectCustomerAccount,
} from 'src/app/state/selectors/customer-account.selector';
import { filter, tap } from 'rxjs/operators';
import { BillingAnalyticsService } from '../services/billing-analytics.service';
import {
  selectExtraCostConfigFile,
  selectExtraCostInfoPagesByProductIds,
} from 'src/app/state/selectors/extra-cost.selectors';
import { getExtraCostConfigFileRequest } from 'src/app/state/actions';
import { Observable } from 'rxjs';

@Component({
  selector: 'my-bill-odb-usage-level-three-page',
  templateUrl: './odb-usage-level-three-page.component.html',
  standalone: false,
})
export class OdbUsageLevelThreePageComponent
  extends OnDestroyHookDirective
  implements OnInit
{
  constructor(
    private router: Router,
    private utilsService: UtilsService,
    private store: Store<ApplicationState>,
    private translationService: TranslationService,
    private environmentService: EnvironmentService,
    private billingAnalyticsService: BillingAnalyticsService
  ) {
    super();
  }

  readonly selectedCustomer$ = this.store.select(selectCustomer);
  readonly customerAccount$ = this.store.select(selectCustomerAccount);
  readonly extraCostCofigFile$ = this.store.select(selectExtraCostConfigFile);

  selectedOdbDetailsInfo?: SelectedOdbDetailsInfo;
  division?: OdbCoverDetailDivision;
  category?: keyof IOdbCoverDetailDivisionSummaryCategories;
  openTransactionToPayIndex?: number;
  documentReference?: string;
  billingAccount?: IBillingAccount;
  hasError: boolean;
  isFetching: boolean;
  productSplit?: Array<IOdbBillProductSplit>;
  billingDocument?: IBillingDocument;
  hasDataAvailable: boolean;
  address?: string;
  section?: IOdbBillCategoryDetailSection;
  selectedSectionProductSplitIdentifier: string;
  duration?: string;
  isStaySignedIn = false;
  showModal = false;
  redirectLink = ONE_DIGITAL_BILL_USAGE_DET_L4_PAGE;
  selectedSectionItemIdentifier: string;
  explanationsIntroPages$?: Observable<string[]>;

  ngOnInit() {
    this.selectedOdbDetailsInfo =
      this.utilsService.getInfoFromSessionStorage<SelectedOdbDetailsInfo>(
        ODB_DETAILS_SESSION_DATA_KEY
      );
    this.billingAccount = this.selectedOdbDetailsInfo?.billingAccount;
    this.documentReference =
      this.selectedOdbDetailsInfo?.billDocument?.reference;

    if (
      this.selectedOdbDetailsInfo !== undefined &&
      this.billingAccount !== undefined &&
      this.documentReference !== undefined
    ) {
      this.hasDataAvailable = true;
      this.division = this.selectedOdbDetailsInfo.division;
      this.category = this.selectedOdbDetailsInfo.category;
      this.openTransactionToPayIndex =
        this.selectedOdbDetailsInfo.openTransactionToPayIndex;
      this.billingDocument = this.selectedOdbDetailsInfo.billDocument;
      this.address = this.selectedOdbDetailsInfo.address;
      this.section = this.selectedOdbDetailsInfo.selectedSection;
      this.selectedSectionItemIdentifier =
        this.selectedOdbDetailsInfo.selectedSectionItemIdentifier;
      this.selectedSectionProductSplitIdentifier =
        this.selectedOdbDetailsInfo.selectedSectionProductSplitIdentifier;
      this.duration = coverService.getCategoryDetailDisplayableDuration(
        this.section?.validFor,
        this.category,
        (key) => this.translationService.getTranslatedValueFor(key)
      );
      this.fetchExtraCostConfigIfNeeded();
      this.getSelectedCustomerAccountStaySignedInStatus();
      this.getPageImpressionTagging();
      this.extractExtraInfoPageKeys();
    } else {
      this.router.navigate([OVERVIEW_PAGE]);
    }
  }

  onUsageItemClick(item: IOdbBillCategoryDetailSectionItem, itemIndex: string) {
    this.utilsService.setValueInSessionStorage<SelectedOdbDetailsInfo>(
      ODB_DETAILS_SESSION_DATA_KEY,
      {
        ...this.utilsService.getInfoFromSessionStorage<SelectedOdbDetailsInfo>(
          ODB_DETAILS_SESSION_DATA_KEY
        ),
        selectedItem: item,
        selectedSectionItemIdentifier: (
          this.selectedSectionItemIdentifier + itemIndex
        ).replace(/\s/g, ''),
      }
    );
    if (this.environmentService.isSmoothAccess || this.isStaySignedIn) {
      this.showModal = true;
    } else {
      this.router.navigate([this.redirectLink]);
    }
  }

  onCancel() {
    this.showModal = false;
  }

  private getSelectedCustomerAccountStaySignedInStatus() {
    this.addSubscription(
      this.customerAccount$
        .pipe(
          filter((customerAccount) => customerAccount !== undefined),
          tap((customerAccount) => {
            this.isStaySignedIn = customerAccount?.staySignedIn ?? false;
          })
        )
        .subscribe()
    );
  }

  private getPageImpressionTagging() {
    this.addSubscription(
      this.customerAccount$
        .pipe(
          filter((customerAccount) => customerAccount !== undefined),
          tap((customerAccount) => {
            this.billingAnalyticsService.reportPageImpressionEventWithPagePrefix(
              'detail.usage',
              this.documentReference,
              this.billingAccount.accountNumber,
              'mybill.detail.usage',
              'mybill.detail.usage.details',
              customerAccount.getSelectedCustomer().customerId,
              customerAccount.getSelectedCustomer().subsegment,
              customerAccount.globalId,
              customerAccount.mobile
            );
          })
        )
        .subscribe()
    );
  }

  private fetchExtraCostConfigIfNeeded() {
    this.addSubscription(
      this.extraCostCofigFile$
        .pipe(
          filter(
            (config) =>
              (!config.hasError &&
                config.extraCostConfig === null &&
                !config.isFetching) ||
              (config.hasError && config.extraCostConfig === null)
          ),
          tap(() => this.store.dispatch(getExtraCostConfigFileRequest()))
        )
        .subscribe()
    );
  }

  private extractExtraInfoPageKeys() {
    const productIdsFromItems = getProductIdsFromSections([this.section]);
    if (productIdsFromItems?.length > 0) {
      this.explanationsIntroPages$ = this.store.select(
        selectExtraCostInfoPagesByProductIds(productIdsFromItems)
      );
    }
  }
}
